import { type ReactNode, useRef, useState } from "react";

import {
  Box,
  Button,
  Column,
  DottedCircleIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Row,
  SearchInput,
  SegmentedControl,
  SegmentedControlOption,
  Text,
} from "@hightouch/htui";

import {
  describeProjectIcon,
  glyphIcon,
  matchesProjectIconSearch,
  PROJECT_EMOJI,
  PROJECT_GLYPHS,
  PROJECT_ICON_COLORS,
  PROJECT_ICON_INK,
  type ProjectIconColor,
  parseProjectIcon,
} from "src/pages/projects/components/project-icon-set";
import { PROJECTS_TERMS as TERMS } from "src/pages/projects/constants";

// Eight fixed 32px columns with a 4px gap. The swatches share them, and the popover takes its width from them.
const GRID_COLUMNS = "repeat(8, 32px)";
const GRID_GAP = 1;
// Seven rows and a peek of the eighth, so the grid reads as scrollable.
const GRID_MAX_HEIGHT = "248px";
// The outer ring is currentColor, so it takes each swatch's own colour.
const SELECTED_SWATCH_RING =
  "0 0 0 2px var(--chakra-colors-white), 0 0 0 4px currentColor";

type ProjectIconProps = {
  icon: string | null | undefined;
  /** The box's size in pixels. The emoji and the glyph scale with it. */
  size?: number;
};

/** A project's coloured glyph or emoji, or nothing when it has none. */
export const ProjectIcon = ({ icon, size = 16 }: ProjectIconProps) => {
  const parsed = parseProjectIcon(icon);
  if (parsed?.kind === "glyph")
    return (
      <Box
        as={parsed.glyph.Icon}
        aria-hidden
        flexShrink={0}
        boxSize={`${size}px`}
        color={parsed.color ?? PROJECT_ICON_INK}
      />
    );
  if (parsed?.kind === "emoji")
    return (
      <Box
        as="span"
        aria-hidden
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        width={`${size}px`}
        height={`${size}px`}
        fontSize={`${Math.round(size * 0.85)}px`}
        lineHeight={1}
      >
        {parsed.emoji}
      </Box>
    );
  return null;
};

type Tab = "emoji" | "icons";

type ProjectIconPickerProps = {
  icon: string | null;
  onChange: (icon: string | null) => void;
  /** The trigger's size in pixels. */
  size?: number;
};

/** The project's icon as a button that opens a picker of emoji and coloured icons. */
export const ProjectIconPicker = ({
  icon,
  onChange,
  size = 40,
}: ProjectIconPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("icons");
  const [query, setQuery] = useState("");
  // The colour for the next glyph while none is picked. It tracks the colour on show, so it survives Clear and an emoji pick.
  const [nextColor, setNextColor] = useState<ProjectIconColor>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const parsed = parseProjectIcon(icon);
  const color = parsed?.kind === "glyph" ? parsed.color : nextColor;
  const described = describeProjectIcon(icon);
  const glyphs = PROJECT_GLYPHS.filter((g) =>
    matchesProjectIconSearch(g, query),
  );
  const emoji = PROJECT_EMOJI.filter((e) => matchesProjectIconSearch(e, query));
  const isEmpty = (tab === "icons" ? glyphs : emoji).length === 0;

  const open = () => {
    if (parsed?.kind === "glyph") setNextColor(parsed.color);
    setTab("icons");
    setQuery("");
    setIsOpen(true);
  };
  const pick = (next: string) => {
    setNextColor(color);
    if (next !== icon) onChange(next);
  };
  const pickColor = (next: ProjectIconColor) => {
    setNextColor(next);
    if (parsed?.kind === "glyph") pick(glyphIcon(parsed.glyph.stem, next));
  };
  const clear = () => {
    setNextColor(color);
    onChange(null);
    // Clear disables itself, which would drop focus to the page and leave Escape with nothing to close.
    searchRef.current?.focus();
  };

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
      initialFocusRef={searchRef}
    >
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          aria-label={
            described
              ? `${TERMS.project.Singular} icon: ${described}. Change icon`
              : `Add ${TERMS.project.singular} icon`
          }
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          width={`${size}px`}
          height={`${size}px`}
          borderRadius="lg"
          bg="base.lightBackground"
          transitionProperty="background-color"
          transitionDuration="fast"
          transitionTimingFunction="easeOutQuad"
          _hover={{ bg: "base.backgroundHover" }}
          onClick={() => (isOpen ? setIsOpen(false) : open())}
        >
          {parsed ? (
            <ProjectIcon icon={icon} size={Math.round(size * 0.55)} />
          ) : (
            // An empty slot, so the button still reads as a place for an icon.
            <Box
              as={DottedCircleIcon}
              aria-hidden
              boxSize={`${Math.round(size * 0.5)}px`}
              color="text.tertiary"
            />
          )}
        </Box>
      </PopoverTrigger>
      {/* The page header card is a z-index 1 stacking context, so in place the grid opens under the board's sticky column headers. */}
      <Portal>
        <PopoverContent
          width="auto"
          p={3}
          aria-label={`${TERMS.project.Singular} icon`}
        >
          <Column gap={3}>
            <Row align="center" justify="space-between" gap={2}>
              <SegmentedControl
                value={tab}
                onChange={(next) => setTab(next)}
                name="project-icon-tab"
              >
                <SegmentedControlOption value="icons">
                  Icons
                </SegmentedControlOption>
                <SegmentedControlOption value="emoji">
                  Emoji
                </SegmentedControlOption>
              </SegmentedControl>
              <Button
                size="sm"
                variant="tertiary"
                isDisabled={!icon}
                onClick={clear}
              >
                Clear
              </Button>
            </Row>
            <SearchInput
              ref={searchRef}
              aria-label={tab === "icons" ? "Search icons" : "Search emoji"}
              placeholder={tab === "icons" ? "Search icons" : "Search emoji"}
              value={query}
              width="100%"
              onChange={(event) => setQuery(event.target.value)}
            />
            {tab === "icons" && (
              <Box
                role="radiogroup"
                aria-label="Icon colour"
                display="grid"
                gridTemplateColumns={GRID_COLUMNS}
                gap={GRID_GAP}
              >
                {PROJECT_ICON_COLORS.map((swatch) => {
                  const isSelected = swatch.value === color;
                  return (
                    <Box
                      key={swatch.label}
                      as="button"
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-label={swatch.label}
                      justifySelf="center"
                      boxSize={6}
                      borderRadius="full"
                      bg={swatch.value ?? PROJECT_ICON_INK}
                      color={swatch.value ?? PROJECT_ICON_INK}
                      boxShadow={isSelected ? SELECTED_SWATCH_RING : undefined}
                      onClick={() => pickColor(swatch.value)}
                    />
                  );
                })}
              </Box>
            )}
            <Box
              role="listbox"
              aria-label={tab === "icons" ? "Icons" : "Emoji"}
              display="grid"
              gridTemplateColumns={GRID_COLUMNS}
              gridAutoRows="32px"
              gap={GRID_GAP}
              maxHeight={GRID_MAX_HEIGHT}
              overflowY="auto"
            >
              {tab === "icons"
                ? glyphs.map((g) => (
                    <GridCell
                      key={g.stem}
                      label={g.label}
                      isSelected={
                        parsed?.kind === "glyph" && parsed.glyph.stem === g.stem
                      }
                      onPick={() => pick(glyphIcon(g.stem, color))}
                    >
                      <Box
                        as={g.Icon}
                        aria-hidden
                        boxSize={5}
                        color={color ?? PROJECT_ICON_INK}
                      />
                    </GridCell>
                  ))
                : emoji.map((e) => (
                    <GridCell
                      key={e.emoji}
                      label={e.label}
                      isSelected={
                        parsed?.kind === "emoji" && parsed.emoji === e.emoji
                      }
                      onPick={() => pick(e.emoji)}
                    >
                      <Box as="span" aria-hidden fontSize="lg" lineHeight={1}>
                        {e.emoji}
                      </Box>
                    </GridCell>
                  ))}
              {isEmpty && (
                <Box gridColumn="1 / -1" py={3} textAlign="center">
                  <Text size="sm" color="text.secondary">
                    Nothing matches
                  </Text>
                </Box>
              )}
            </Box>
          </Column>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

type GridCellProps = {
  label: string;
  isSelected: boolean;
  onPick: () => void;
  children: ReactNode;
};

const GridCell = ({ label, isSelected, onPick, children }: GridCellProps) => (
  <Box
    as="button"
    type="button"
    role="option"
    aria-selected={isSelected}
    aria-label={label}
    title={label}
    display="flex"
    alignItems="center"
    justifyContent="center"
    borderRadius="md"
    bg={isSelected ? "base.backgroundPressed" : undefined}
    _hover={{
      bg: isSelected ? "base.backgroundPressed" : "base.backgroundHover",
    }}
    onClick={onPick}
  >
    {children}
  </Box>
);
